import { Button, Flex, FlexProps, Typography } from 'antd'
import React from 'react'
import { Candidate } from '../candidate'
import { CandidateAttributes } from './candidate-attributes'
import { CandidateAvatar } from './candidate-avatar'
import { CheckOutlined, CommentOutlined } from '@ant-design/icons'
import { TimeAgo } from '../../components/time-ago'
import { useAuth } from '../../auth'
import { CandidateCompatibility } from './candidate-compatibility'
import clsx from 'clsx'

type CandidateCardProps = {
    active?: boolean
    candidate: Candidate
    selected?: boolean
    onSelected?: (id: string) => void
    onDeselected?: (id: string) => void
    collapsed?: boolean
    showPhone?: boolean
    className?: string
}

export const CandidateCard = React.memo(
    function ({
        active = false,
        candidate,
        selected,
        onSelected,
        onDeselected,
        collapsed = true,
        showPhone = false,
        className,
        ...rest
    }: CandidateCardProps & Omit<FlexProps, 'children'>) {
        const { showCandidateTimestamp } = useAuth()
        return (
            <Flex
                gap="small"
                className={clsx(
                    'text-left hover:bg-wash-accent rounded-lg p-2',
                    {
                        'bg-wash-accent': active,
                    },
                    className
                )}
                {...rest}
            >
                {selected ? (
                    <div>
                        <Button
                            icon={<CheckOutlined className="text-primary" />}
                            onClick={(event) => {
                                if (!onDeselected) {
                                    return
                                }

                                event.stopPropagation()
                                event.preventDefault()

                                onDeselected(candidate.id)
                            }}
                            size="small"
                            className="border-primary border-2"
                            style={{ width: 30, height: 30 }}
                        />
                    </div>
                ) : (
                    <CandidateAvatar
                        size={30}
                        className="cursor-pointer"
                        onClick={(event) => {
                            if (!event || !onSelected) {
                                return
                            }

                            event.stopPropagation()
                            event.preventDefault()

                            onSelected(candidate.id)
                        }}
                        candidate={candidate}
                    />
                )}

                <Flex
                    vertical
                    className="w-full"
                >
                    <Flex
                        justify="space-between"
                        align="center"
                    >
                        <Typography.Text
                            ellipsis
                            title={candidate.name}
                            className={clsx('mb-0 block max-w-[165px]', {
                                'font-medium': active,
                            })}
                        >
                            {candidate.name}
                        </Typography.Text>
                        <CandidateCompatibility candidate={candidate} />
                    </Flex>
                    <Flex vertical>
                        {candidate.campaign && (
                            <Typography.Text
                                type="secondary"
                                className="max-w-[220px]"
                                ellipsis
                            >
                                {candidate.campaign.title}
                            </Typography.Text>
                        )}
                        {showPhone && (
                            <Typography.Link
                                type="secondary"
                                href={`tel:${candidate.phone}`}
                                className="max-w-[220px]"
                                ellipsis
                                onClick={(event) => {
                                    event.stopPropagation()
                                }}
                            >
                                {candidate.phone}
                            </Typography.Link>
                        )}
                        {candidate.commentCount !== 0 && (
                            <Typography.Text type="secondary">
                                <CommentOutlined /> {candidate.commentCount}
                            </Typography.Text>
                        )}
                        {showCandidateTimestamp && (
                            <Typography.Text type="secondary">
                                <TimeAgo date={candidate.createdAt} />
                            </Typography.Text>
                        )}
                    </Flex>

                    {!collapsed && candidate.compatibility !== null && (
                        <div
                            className="text-xs text-black mt-1"
                            style={{ opacity: active ? 1 : 0.7 }}
                        >
                            <CandidateAttributes
                                positive={candidate.positive}
                                negative={candidate.negative}
                            />
                        </div>
                    )}
                </Flex>
            </Flex>
        )
    },
    (prevProps, nextProps) => {
        return (
            prevProps.selected === nextProps.selected &&
            prevProps.candidate === nextProps.candidate &&
            prevProps.active === nextProps.active &&
            prevProps.collapsed === nextProps.collapsed &&
            prevProps.showPhone === nextProps.showPhone
        )
    }
)
